import React from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { FirmwareItem } from '../../types/otaUpdate';
import { useFirmwareDetail } from './firmwareDetail/useFirmwareDetail';
import { FirmwareDeploymentSection, FirmwareFileSection, FirmwareIdentitySection, FirmwareMetadataSection } from './firmwareDetail/FirmwareDetailSections';
import './FirmwareDetailModal.css';

interface FirmwareDetailModalProps {
  isOpen: boolean;
  firmware: FirmwareItem | null;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
}

/**
 * One firmware: identity, file, deployment and dates. Read first; "Edit"
 * turns the cells into controls. The state lives in `firmwareDetail/`.
 */
export const FirmwareDetailModal: React.FC<FirmwareDetailModalProps> = ({ isOpen, firmware, onClose, onSave, onDelete }) => {
  const { t } = useI18n();
  const state = useFirmwareDetail({ firmware, isOpen, onClose, onSave, onDelete });

  if (!isOpen || !firmware) return null;

  const { editing, busy } = state;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="firmware-detail-modal" data-testid="modal-firmware-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-title-section">
            <h2>{t('otaUpdates.firmwareDetails')}</h2>
            <span className={`status-indicator ${firmware.is_active ? 'active' : 'inactive'}`}>{firmware.is_active ? t('common.active') : t('common.inactive')}</span>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        {state.error && (
          <div className="error-banner">
            <span>{state.error}</span>
            <button type="button" onClick={state.dismissError} aria-label={t('common.dismissError')}>×</button>
          </div>
        )}

        {state.confirmingDelete && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm-dialog">
              <span className="delete-confirm-icon">!</span>
              <p>{t('otaUpdates.deleteConfirm')}</p>
              <div className="delete-confirm-actions">
                <button type="button" className="btn btn-secondary" onClick={() => state.setConfirmingDelete(false)} disabled={busy}>{t('common.cancel')}</button>
                <button type="button" className="btn btn-danger" onClick={state.remove} disabled={busy}>{busy ? t('otaUpdates.deleting') : t('common.delete')}</button>
              </div>
            </div>
          </div>
        )}

        <div className="modal-content">
          <FirmwareIdentitySection firmware={firmware} state={state} />
          <FirmwareFileSection firmware={firmware} state={state} />
          <FirmwareDeploymentSection firmware={firmware} state={state} />
          <FirmwareMetadataSection firmware={firmware} />
        </div>

        <div className="modal-actions">
          <div className="action-group-left">
            {!editing && (
              <button type="button" className="btn btn-danger-outline" data-testid="btn-firmware-delete" onClick={() => state.setConfirmingDelete(true)} disabled={busy || state.confirmingDelete}>
                {t('common.delete')}
              </button>
            )}
          </div>
          <div className="action-group-right">
            {editing ? (
              <>
                <button type="button" className="btn btn-secondary" data-testid="btn-firmware-cancel" onClick={state.cancelEdit} disabled={busy}>{t('common.cancel')}</button>
                <button type="button" className="btn btn-success" data-testid="btn-firmware-save" onClick={state.save} disabled={busy}>{busy ? t('otaUpdates.saving') : t('otaUpdates.saveChanges')}</button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-secondary" data-testid="btn-firmware-close" onClick={onClose}>{t('common.close')}</button>
                <button type="button" className="btn btn-primary" data-testid="btn-firmware-edit" onClick={state.startEdit}>{t('common.edit')}</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
